import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import type { TorqueToolCommanderCfgSchema } from '@redviking/argonaut-util/types/mes/applet-designs/latest/flow/nodes/torque-tool-commander.zod';
import { ArgoConnectionKindEnum } from '@redviking/argonaut-util/types/db-enums/argo/connection_kind';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import type { NodeSpec } from './NodeSpec';
import type { NodeSocketKindMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { defaultControlUpdateValueHandler } from '@redviking/argonaut-core-ui/src/applet/design/flows/controls/control-event-handler';

const commandItems = [
    { label: 'Set Vin', value: 'setVin' },
    { label: 'Set Batch', value: 'setBatch' },
    { label: 'Set Parameter Set ID', value: 'setPSetId' },
] satisfies { label: string; value: TorqueToolCommanderCfgSchema['controlValues']['command'] }[];

export const torqueToolCommanderNodeSpec: NodeSpec<NodeCfg<'torqueToolCommander'>> = {
    nodeCategory: 'connection',
    nodeSubCategory: 'Torque Tool',
    requiredSockets: (_node, inputs, outputs) => ({ required: [], conditionallyRequired: [ { kind: 'or', availableOptions: [ ...inputs, ...outputs ] as (keyof NodeSocketKindMap['torqueToolCommander']['inputs'] | keyof NodeSocketKindMap['torqueToolCommander']['outputs'])[] } ] }),
    kindLabel: 'Torque Tool Commander',
    cfg: () => ({ id: getUID(), kind: 'torqueToolCommander', width: 220, controlValues: { connectionId: '', command: 'setPSetId' } }),
    controls: nodeCfg => ({
        connectionId: defineControlOpts({
            cfg: {
                kind: 'connectionSelect',
                label: 'Connection',
                allowedConnectionKinds: [ ArgoConnectionKindEnum.TorqueTool ],
            },
            on: { 'update:value': defaultControlUpdateValueHandler },
            value: nodeCfg.controlValues.connectionId,
        }),
        command: defineControlOpts({
            cfg: {
                kind: 'selectWithCallback',
                label: 'Command',
                selectLabel: 'Command',
                items: commandItems,
            },
            on: {
                'update:value': () => {}, // no-op, this control handles updates via the select callback
                'select': (value, { flowEventEmitter }) => {
                    const command = value as TorqueToolCommanderCfgSchema['controlValues']['command'];
                    // sockets are declarative (see `inputs`), so switching commands automatically drops any now-invalid connections
                    flowEventEmitter.emit('updateNodeCfg', {
                        newNodeCfg: { ...nodeCfg, controlValues: { ...nodeCfg.controlValues, command } },
                    });
                },
            },
            value: nodeCfg.controlValues.command,
        }),
    }),
    inputs: nodeCfg => {
        const inputs: ReturnType<NodeSpec<NodeCfg<'torqueToolCommander'>>['inputs']> = {
            writeData: { socket: 'event', label: 'Write Data' },
        };
        const addNumber = (key: 'pSetId' | 'batchCount', label: string) => {
            inputs[key] = {
                socket: 'number',
                label,
                controlOpts: defineControlOpts({ cfg: { kind: 'number' }, on: { 'update:value': defaultControlUpdateValueHandler }, value: nodeCfg.socketDefaults?.inputs?.[key], enableWithConnectedSocket: false }),
            };
        };
        if (nodeCfg.controlValues.command === 'setBatch') {
            addNumber('pSetId', 'Parameter Set ID');
            addNumber('batchCount', 'Batch Count');
        } else if (nodeCfg.controlValues.command === 'setPSetId') {
            addNumber('pSetId', 'Parameter Set ID');
        } else {
            inputs.vin = { socket: 'string', label: 'VIN', controlOpts: defineControlOpts({ cfg: { kind: 'text' }, on: { 'update:value': defaultControlUpdateValueHandler }, value: nodeCfg.socketDefaults?.inputs?.vin, enableWithConnectedSocket: false }) };
        }
        return inputs;
    },
    outputs: () => ({ next: { socket: 'event', label: 'Next' } }),
    validator: node => node.controlValues.connectionId ? true : { connectionId: 'Connection id is required' },
};
